import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  attachApiErrorToast,
  notifyApiSuccess,
  notifyApiSuccessFrom,
} from "../../helpers/apiNotify";
import qs from "qs";
import _ from "lodash";
import { Endpoints } from "../../constants/apiEndPoints";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});
attachApiErrorToast(API, { fallback: "Company request failed" });

// Async thunk for creating a company
export const createCompany = createAsyncThunk(
  "companies/createCompany",
  async (data, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${data.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.post("/company/", data.companyData, config);
      notifyApiSuccessFrom(response, "Create Successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create company"
      );
    }
  }
);

// Async thunk for updating a company
export const updateCompany = createAsyncThunk(
  "companies/updateCompany",
  async ({ companyId, companyData, accessToken }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.put(
        `/company/${companyId}`,
        companyData,
        config
      );
      notifyApiSuccessFrom(response, "Update Successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to update company"
      );
    }
  }
);

// Async thunk for deleting a company
export const deleteCompany = createAsyncThunk(
  "companies/deleteCompany",
  async ({ configdata }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${configdata.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.delete(
        `/company/${configdata.company_id}`,
        config
      );
      // Toast here — list rows are company_admins (user.id ≠ company_id)
      notifyApiSuccessFrom(response, "Delete Successfully");
      return {
        companyId: configdata.company_id,
        isRestore: false,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.reason ||
          "Failed to delete company"
      );
    }
  }
);

// PATCH /company/:id/restore — reactivates company and linked user
export const restoreCompany = createAsyncThunk(
  "companies/restoreCompany",
  async ({ configdata }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${configdata.accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.patch(
        `/company/${configdata.company_id}/restore`,
        {},
        config
      );
      notifyApiSuccessFrom(response, "Company restored successfully.");
      return {
        companyId: configdata.company_id,
        isRestore: true,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.reason ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to restore company"
      );
    }
  }
);

// Async thunk for fetching companies
export const fetchCompanies = createAsyncThunk(
  "companies/fetchCompanies",
  async ({ accessToken, queryParams }, { rejectWithValue }) => {
    try {
      let url = "/users?roles=company_admin";
      if (queryParams && !_.isEmpty(queryParams)) {
        url += `&${queryParams instanceof Object
          ? qs.stringify(queryParams)
          : queryParams
          }`;
      }
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.get(url, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch companies"
      );
    }
  }
);

// Async thunk for resending activation / invite email
// Backend route: POST /users/re-activation { email, company_id }
export const resendEmail = createAsyncThunk(
  "companies/resendEmail",
  async ({ authToken, email, company_id }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${authToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.post(
        Endpoints.reActivation,
        { email, company_id },
        config
      );
      // Force resend UX copy — API often returns "User reactivated successfully"
      notifyApiSuccess("Email sent successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to resend email"
      );
    }
  }
);

// Async thunk for fetching company by id
export const fetchCompanyById = createAsyncThunk(
  "companies/fetchCompanyById",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      const response = await API.get(`/company/${data.companyId}`, config);
      return response.data;
    } catch (error) {
      throw new Error("Failed to load company");
    }
  }
);

// Async thunk for fetching company applications
export const fetchCompanyApplications = createAsyncThunk(
  "companies/fetchCompanyApplications",
  async (data) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${data.accessToken}`,
        },
      };
      const response = await API.get(
        `/company/${data.companyId}/applications`,
        config
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load company applications");
    }
  }
);

/**
 * Fetches all company operators (company_admin users) — same source as the Companies table.
 * Includes soft-deleted rows so dashboard "All Operators" matches the table total.
 */
export const fetchAllCompaniesForCounts = createAsyncThunk(
  "companies/fetchAllCompaniesForCounts",
  async ({ accessToken }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          "Content-Type": "application/json",
          Authorization: `${accessToken}`,
        },
      };

      let allCompanies = [];
      let currentPage = 1;
      let hasMorePages = true;
      const pageSize = 100;

      while (hasMorePages) {
        // Match Companies page: GET /users?roles=company_admin
        const url = `/users?roles=company_admin&page=${currentPage}&limit=${pageSize}`;
        const response = await API.get(url, config);
        const responseData = response.data;

        let pageCompanies = [];
        if (Array.isArray(responseData)) {
          pageCompanies = responseData;
        } else if (Array.isArray(responseData?.data)) {
          pageCompanies = responseData.data;
        } else if (Array.isArray(responseData?.users)) {
          pageCompanies = responseData.users;
        } else if (Array.isArray(responseData?.companies)) {
          pageCompanies = responseData.companies;
        }

        allCompanies = allCompanies.concat(pageCompanies);

        const totalPages =
          responseData?.totalPages ||
          responseData?.pagination?.total_pages ||
          responseData?.pagination?.totalPages ||
          responseData?.last_page ||
          null;

        if (totalPages != null) {
          hasMorePages = currentPage < totalPages;
        } else if (pageCompanies.length < pageSize) {
          hasMorePages = false;
        } else {
          hasMorePages = currentPage < 50;
        }
        currentPage += 1;
      }

      return allCompanies;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all companies"
      );
    }
  }
);

// Async thunk for reactivating a user
export const reactivateUser = createAsyncThunk(
  "companies/reactivateUser",
  async ({ email, company_id, accessToken }) => {
    try {
      const config = {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      };
      const response = await API.post(
        Endpoints.reActivation,
        { email, company_id },
        config
      );
      notifyApiSuccessFrom(response, "User reactivated successfully");
      return response.data;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to reactivate user";
      throw new Error(errorMsg);
    }
  }
);

const initialState = {
  companies: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  fetchLoading: false,
  applicationsLoading: false,
  fetchError: null,
  company: {},
  applications: [],
  allCompanies: [],
  allCompaniesLoading: false,
};

const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createCompany.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.companies) && action.payload) {
          state.companies = [action.payload, ...state.companies];
        }
        state.error = null;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.companies.findIndex(
          (company) => company.id === action.payload.id
        );
        if (index !== -1) {
          state.companies[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(deleteCompany.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.loading = false;
        const companyId =
          action.payload?.companyId ?? action.payload;
        state.companies = (state.companies || []).map((company) => {
          const matches =
            company.company_id === companyId ||
            company.company?.id === companyId ||
            company.id === companyId;
          if (matches) {
            return { ...company, is_deleted: true, active: false };
          }
          return company;
        });
        state.error = null;
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(restoreCompany.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreCompany.fulfilled, (state, action) => {
        state.loading = false;
        const companyId =
          action.payload?.companyId ?? action.payload;
        state.companies = (state.companies || []).map((company) => {
          const matches =
            company.company_id === companyId ||
            company.company?.id === companyId ||
            company.id === companyId;
          if (matches) {
            return { ...company, is_deleted: false, active: true };
          }
          return company;
        });
        state.error = null;
      })
      .addCase(restoreCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.companies = action.payload;
          // For array responses, calculate pagination based on length
          const totalPages = Math.ceil(action.payload.length / 10);
          state.pagination = {
            currentPage: 1,
            totalPages: totalPages || 1,
            totalItems: action.payload.length,
            itemsPerPage: 10,
          };
        } else {
          state.companies = action.payload.data || action.payload.companies || action.payload.users || [];

          // Helper to normalize pagination data
          const getPagination = (source) => ({
            currentPage: source.current_page || source.currentPage || source.page || 1,
            totalPages: source.total_pages || source.totalPages || source.last_page || 1,
            totalItems: source.total || source.totalRecords || source.totalItems || 0,
            itemsPerPage: source.per_page || source.pageSize || source.limit || 10,
          });

          const paginationData = action.payload.pagination || action.payload.meta; // Check meta as well

          if (paginationData) {
            state.pagination = getPagination(paginationData);
          } else if (action.payload.current_page || action.payload.total_pages || action.payload.last_page) {
            // Check if pagination is at the root
            state.pagination = getPagination(action.payload);
          } else {
            console.warn("Pagination Debug - No metadata found, falling back"); // Debug Log
            // Fallback pagination if no metadata provided
            const totalPages = Math.ceil(state.companies.length / 10);
            state.pagination = {
              currentPage: 1,
              totalPages: totalPages || 1,
              totalItems: state.companies.length,
              itemsPerPage: 10,
            };
          }
        }
        state.error = null;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resendEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendEmail.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resendEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanyById.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.company = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.error?.message;
        state.company = initialState.company;
        state.error = null;
      })
      .addCase(fetchCompanyApplications.pending, (state) => {
        state.applicationsLoading = true;
      })
      .addCase(fetchCompanyApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        // Handle both array response and nested structures safely
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.applications = payload;
        } else if (Array.isArray(payload?.applications)) {
          state.applications = payload.applications;
        } else if (Array.isArray(payload?.data)) {
          state.applications = payload.data;
        } else {
          state.applications = [];
        }
        state.fetchError = null;
      })
      .addCase(fetchCompanyApplications.rejected, (state, action) => {
        state.applicationsLoading = false;
        state.fetchError = action.error?.message;
        state.applications = initialState.applications;
        state.error = null;
      })
      .addCase(fetchAllCompaniesForCounts.pending, (state) => {
        state.allCompaniesLoading = true;
      })
      .addCase(fetchAllCompaniesForCounts.fulfilled, (state, action) => {
        state.allCompaniesLoading = false;
        state.allCompanies = action.payload;
        state.error = null;
      })
      .addCase(fetchAllCompaniesForCounts.rejected, (state, action) => {
        state.allCompaniesLoading = false;
        state.error = action.payload;
      });
  },
});

export default companiesSlice.reducer;
